import { z } from 'zod';
import { ASSESSMENT_KINDS, DECISIONS } from '../lmd/engine';
import { REGIMES } from '../lmd/structure';
import { TenantSettingsSchema } from '../settings';

export const SESSION_KINDS = ['principale', 'rattrapage'] as const;
export const SessionKindSchema = z.enum(SESSION_KINDS);
export const GradingRulesSchema = TenantSettingsSchema.shape.grading;

/** 0–20 with at most two decimals. */
export const GradeValueSchema = z
  .number()
  .min(0, 'grades.errors.range')
  .max(20, 'grades.errors.range')
  .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, 'grades.errors.decimals');

export const ExamSessionSchema = z.object({
  id: z.uuid(),
  academicYearId: z.uuid(),
  parity: z.enum(['odd', 'even']),
  kind: SessionKindSchema,
  status: z.enum(['open', 'closed']),
});
export type ExamSession = z.infer<typeof ExamSessionSchema>;

export const SheetStatusSchema = z.enum(['brouillon', 'transmis']);

export const GradeSheetQuerySchema = z.object({
  yearId: z.uuid(),
  sessionId: z.uuid().optional(),
  levelId: z.uuid().optional(),
  status: SheetStatusSchema.optional(),
  mine: z.coerce.boolean().optional(),
});

export const GradeSheetRowSchema = z.object({
  id: z.uuid(),
  courseName: z.string(),
  unitCode: z.string(),
  unitName: z.string(),
  semesterIndex: z.number().int(),
  levelCode: z.string(),
  groupName: z.string(),
  programName: z.string(),
  session: SessionKindSchema,
  teacherName: z.string().nullable(),
  status: SheetStatusSchema,
  /** Marks entered (value or ABS) / marks expected. */
  filled: z.number().int(),
  expected: z.number().int(),
  students: z.number().int(),
  lockedAt: z.string().nullable(),
});
export type GradeSheetRow = z.infer<typeof GradeSheetRowSchema>;

export const MarkSchema = z.object({ value: z.number().nullable(), absent: z.boolean() });

export const CorrectionSchema = z.object({
  id: z.uuid(),
  studentId: z.uuid(),
  assessmentId: z.uuid(),
  previous: MarkSchema,
  requested: MarkSchema,
  reason: z.string(),
  status: z.enum(['pending', 'approved', 'rejected']),
  requestedBy: z.string(),
  createdAt: z.string(),
});
export type Correction = z.infer<typeof CorrectionSchema>;

export const GradeSheetDetailSchema = z.object({
  id: z.uuid(),
  courseName: z.string(),
  unitCode: z.string(),
  unitName: z.string(),
  semesterIndex: z.number().int(),
  levelCode: z.string(),
  groupName: z.string(),
  session: SessionKindSchema,
  regime: z.enum(REGIMES),
  teacherName: z.string().nullable(),
  status: SheetStatusSchema,
  lockedAt: z.string().nullable(),
  updatedAt: z.string(),
  columns: z.array(z.object({ id: z.uuid(), kind: z.enum(ASSESSMENT_KINDS), weight: z.number() })),
  students: z.array(
    z.object({
      studentId: z.uuid(),
      matricule: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      /** Over the absence elimination threshold in this ECUE (principal session): not acquired, retake. */
      eliminated: z.boolean(),
      /** The student asked for this mark to be checked and the request is not answered yet. */
      reviewPending: z.boolean(),
      /** By assessment id. */
      marks: z.record(z.string(), MarkSchema),
    }),
  ),
  rules: GradingRulesSchema,
  corrections: z.array(CorrectionSchema),
  can: z.object({
    edit: z.boolean(),
    lock: z.boolean(),
    unlock: z.boolean(),
    requestCorrection: z.boolean(),
    decideCorrections: z.boolean(),
  }),
});
export type GradeSheetDetail = z.infer<typeof GradeSheetDetailSchema>;

export const GradeChangeSchema = z
  .object({
    assessmentId: z.uuid(),
    studentId: z.uuid(),
    value: GradeValueSchema.nullable(),
    absent: z.boolean(),
  })
  .refine((c) => !(c.absent && c.value !== null), { message: 'grades.errors.absentWithValue' });
export const GradeChangesSchema = z.object({ changes: z.array(GradeChangeSchema).min(1).max(2000) });
export type GradeChange = z.infer<typeof GradeChangeSchema>;

export const SaveResultSchema = z.object({ saved: z.number().int(), updatedAt: z.string() });
export type SaveResult = z.infer<typeof SaveResultSchema>;

export const ImportReportSchema = z.object({
  rows: z.number().int(),
  recognized: z.number().int(),
  unknownMatricules: z.array(z.string()),
  invalid: z.array(z.object({ row: z.number().int(), column: z.string(), value: z.string() })),
  /** Empty cells of the sheet that the file fills. */
  fillable: z.number().int(),
  /** Cells already filled in the sheet: never overwritten by an import. */
  skippedExisting: z.number().int(),
  applied: z.boolean(),
});
export type ImportReport = z.infer<typeof ImportReportSchema>;

/** An Excel import processed by the worker (preview first, then apply). */
export const GradeImportSchema = z.object({
  id: z.uuid(),
  gradeSheetId: z.uuid(),
  fileName: z.string(),
  apply: z.boolean(),
  status: z.enum(['pending', 'done', 'failed']),
  report: ImportReportSchema.nullable(),
  /** i18n reason: file | columns | locked | internal */
  error: z.string().nullable(),
});
export type GradeImport = z.infer<typeof GradeImportSchema>;

export const CorrectionInputSchema = z.object({
  studentId: z.uuid(),
  assessmentId: z.uuid(),
  value: GradeValueSchema.nullable(),
  absent: z.boolean(),
  reason: z.string().trim().min(5, 'grades.errors.reason').max(500),
});
export type CorrectionInput = z.infer<typeof CorrectionInputSchema>;

export const DecideCorrectionSchema = z.object({
  approve: z.boolean(),
  note: z.string().trim().max(500).optional(),
});

export const JurySummarySchema = z.object({
  id: z.uuid(),
  levelCode: z.string(),
  levelLabel: z.string(),
  programName: z.string(),
  semesterIndex: z.number().int(),
  students: z.number().int(),
  sheetsTotal: z.number().int(),
  sheetsLocked: z.number().int(),
  principaleStatus: z.enum(['open', 'generated']),
  rattrapageStatus: z.enum(['open', 'generated']),
});
export type JurySummary = z.infer<typeof JurySummarySchema>;

export const JuryRowSchema = z.object({
  enrollmentId: z.uuid(),
  studentId: z.uuid(),
  matricule: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  units: z.array(
    z.object({
      unitId: z.uuid(),
      average: z.number().nullable(),
      acquired: z.boolean(),
      compensated: z.boolean(),
      eliminatory: z.boolean(),
    }),
  ),
  complete: z.boolean(),
  average: z.number().nullable(),
  creditsAcquired: z.number().int(),
  creditsTotal: z.number().int(),
  computedDecision: z.enum(DECISIONS).nullable(),
  /** Final decision: the jury override when present, else the computed one. */
  decision: z.enum(DECISIONS).nullable(),
  mention: z.string().nullable(),
  compensatedUnits: z.number().int(),
  compensationBlockedBy: z.enum(['eliminatory', 'average', 'disabled']).nullable(),
  override: z.object({ decision: z.enum(DECISIONS), reason: z.string() }).nullable(),
});
export type JuryRow = z.infer<typeof JuryRowSchema>;

export const JuryViewSchema = z.object({
  id: z.uuid(),
  session: SessionKindSchema,
  yearLabel: z.string(),
  levelCode: z.string(),
  programName: z.string(),
  semesterIndex: z.number().int(),
  members: z.array(z.object({ name: z.string(), role: z.string() })),
  units: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string(), credits: z.number().int() })),
  rows: z.array(JuryRowSchema),
  stats: z.object({
    students: z.number().int(),
    admitted: z.number().int(),
    rattrapage: z.number().int(),
    ajournes: z.number().int(),
    incomplete: z.number().int(),
  }),
  sheets: z.object({
    total: z.number().int(),
    locked: z.number().int(),
    pending: z.array(
      z.object({
        id: z.uuid(),
        courseName: z.string(),
        groupName: z.string(),
        teacherName: z.string().nullable(),
      }),
    ),
  }),
  status: z.enum(['open', 'generated']),
  /** Rattrapage: the principal session PV must exist (it fixes who sits the retake). */
  principaleGenerated: z.boolean(),
  published: z.boolean(),
  pvDocumentId: z.uuid().nullable(),
  /** Rattrapage session: grade sheets have been prepared for the students concerned. */
  rattrapageSheets: z.number().int(),
  rules: z.object({
    eliminatoryUeGrade: z.number().nullable(),
    minAverage: z.number(),
    compensation: z.boolean(),
  }),
});
export type JuryView = z.infer<typeof JuryViewSchema>;

export const JuryQuerySchema = z.object({ session: SessionKindSchema.default('principale') });

export const OverrideDecisionSchema = z.object({
  session: SessionKindSchema,
  decision: z.enum(DECISIONS).nullable(),
  reason: z.string().trim().min(5, 'deliberation.errors.reason').max(500),
});

export const GeneratePvSchema = z.object({
  session: SessionKindSchema,
  includeStats: z.boolean().default(true),
  signatures: z.boolean().default(true),
  publish: z.boolean().default(false),
});

export const StudentResultsQuerySchema = z.object({
  enrollmentId: z.uuid().optional(),
  semester: z.coerce.number().int().min(1).max(12).optional(),
});

export const StudentResultsSchema = z.object({
  semesters: z.array(z.object({ enrollmentId: z.uuid(), index: z.number().int(), yearLabel: z.string() })),
  selected: z.object({ enrollmentId: z.uuid(), index: z.number().int() }).nullable(),
  session: SessionKindSchema,
  /** Frozen PV decision when available, else the live computation. */
  frozen: z.boolean(),
  passMark: z.number(),
  average: z.number().nullable(),
  creditsAcquired: z.number().int(),
  creditsTotal: z.number().int(),
  decision: z.enum(DECISIONS).nullable(),
  mention: z.string().nullable(),
  /** Student space: last day (ISO) to ask for a mark of this session to be checked; null = closed. */
  reviewUntil: z.string().nullable(),
  units: z.array(
    z.object({
      id: z.uuid(),
      code: z.string(),
      name: z.string(),
      nameAr: z.string().nullable(),
      credits: z.number().int(),
      average: z.number().nullable(),
      acquired: z.boolean(),
      compensated: z.boolean(),
      courses: z.array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          nameAr: z.string().nullable(),
          coefficient: z.number(),
          credits: z.number().int(),
          average: z.number().nullable(),
        }),
      ),
    }),
  ),
});
export type StudentResults = z.infer<typeof StudentResultsSchema>;
